import {
  fileCategoryLabels, offerSigningCompletion, signedBeforeDeadline, signedOfferStage,
  type HrWorkflowRecord, type LifecycleState, type OfferTerms, type SignedOfferStage, type StepInstance,
} from '@vigeo/shared';

/**
 * The words rows 2.1 and 2.2 say, kept apart from the components that lay them out.
 *
 * Same split as `features/offers/model.ts` and for the same reason: every sentence on these two
 * screens is built here, so the wording Paul settles on changes in one file and the tests can
 * read it without rendering anything. Copy is verbatim from the approved frames 06b `321:1066`
 * (2.1 Review the signed offer) and `322:1186` (2.2 Move to Onboarding), with the candidate's
 * own name substituted where the frame writes "Marcus".
 */

/** The offer's four states, in the order the frame's tracker draws them. */
export const offerTrackerSteps = ['Draft', 'Reviewed', 'Sent', 'Signed'] as const;

/** The lifecycle strip on 2.2, with the frame's own captions. */
export interface StageStep {
  readonly label: string;
  readonly caption: string;
}

/**
 * Note the third caption. `lifecyclePresenters.active.description` says "Employed and
 * assignable." — what being Active MEANS. The frame says "After orientation is completed" — how
 * somebody GETS there, which is the useful thing to read while deciding to move them one stage
 * short of it. Both are right about different questions, and the frame is the source of truth for
 * this strip, so its words are used here rather than the registry's.
 */
export const stageSteps: readonly StageStep[] = [
  { label: 'Candidate', caption: 'In the pipeline, not hired' },
  { label: 'Onboarding', caption: 'Offer accepted, working through requirements' },
  { label: 'Active', caption: 'After orientation is completed' },
];

/**
 * WHICH STAGE STEP A PERSON HAS REACHED, for the read-only strip on the post-move card (#346).
 *
 * `stageSteps` draws the three the frame draws, and the lifecycle has six. The three that are not
 * on the strip are answered by the last strip stage the person actually got to, because that is
 * what the strip is claiming: `withdrawn` never went past Candidate, and `inactive` and `separated`
 * are both after Active. Returning 0 for all of them would say a separated employee never started.
 */
export function stageReachedFor(lifecycle: LifecycleState): number {
  switch (lifecycle) {
    case 'candidate':
    case 'withdrawn':
      return 0;
    case 'onboarding':
      return 1;
    case 'active':
    case 'inactive':
    case 'separated':
      return 2;
    default:
      return 0;
  }
}

/** The post-move card's own words. Kept here with the rest of the screen's copy. */
export const outcomeCopy = {
  proposedStart: 'Proposed start',
  /* Says where the reason is rather than printing it: see the note in OfferOutcome.tsx. */
  whereTheReasonIs: 'The move and the reason given for it are on the timeline, under History.',
  whereTheNextMoveIs: 'The next stage change is made in Orientation and start.',

  /**
   * 2.5, the read-only "Hired on" summary (Paul, 2026-09-29 15:45; #373).
   *
   * The banner is the DURABLE counterpart to 2.4: 2.4 confirms a send to whoever performed it and
   * then goes away, while this says the same fact to anyone who opens the record afterwards. That is
   * why 2.4 can be transient and this cannot.
   */
  /*
   * TRUE FROM THE LIFECYCLE ALONE, which a review of #396 doubted and the API settles: entering
   * onboarding is what creates the packet. `apps/api/src/functions/people.ts` issues it on the same
   * request that writes the lifecycle — "Entering onboarding is what creates the packet: HR moves
   * someone to onboarding and their work items exist" — and `people/{id}/lifecycle` is the only
   * route that can set `onboarding`. So every person this banner renders for has their items.
   *
   * The EMAIL is the part that does not follow, and it is a separate sentence below for that reason:
   * 2.2 defaults its checkbox to off, so the ordinary move sends no mail at all.
   */
  sentBanner: 'Onboarding items have been sent to their portal.',
  /**
   * "SEND", NOT PAUL'S "RESEND", AND THAT IS A DELIBERATE DEVIATION — recorded on #373.
   *
   * #373 specifies "Resend Welcome email". That word is a claim about the record: it says an email
   * already went to this person. On the ordinary path it is false — 2.2's Welcome email checkbox
   * starts unticked, so moving somebody to onboarding sends no mail — and a review of #396 found the
   * button offering to *re*send to people who had never been emailed, then reporting it "queued" as
   * though it were the second copy.
   *
   * The conditional label was the obvious fix and it cannot be built honestly today: a welcome
   * email is an agency settings template rendered into a plain message, and every message
   * `sendCommunication` stores carries `templateId: 'custom'` (`apps/api/src/functions/
   * communications.ts`), so nothing marks one as the Welcome email. Matching on the subject looks
   * like a signal and is not — subjects are stored RENDERED, after merge tokens are filled, so a
   * template subject carrying `{{firstName}}` would never match and the check would read "never
   * sent" for everybody, for ever, while appearing to work.
   *
   * So the button says the thing that is true in both cases. Making Paul's word available needs a
   * durable marker on the communication, which is its own change to a deliberately closed mail
   * vocabulary and not something to slip into a review fix.
   */
  resendWelcome: 'Send Welcome email',
  resending: 'Sending…',
  resent: 'Welcome email queued.',
  /** The signed offer itself, beside the terms it agreed. */
  viewSignedOffer: 'View signed offer',
  /**
   * "HIRING PROGRESS", NOT "STAGE" (Paul, 2026-09-29; #388).
   *
   * The strip was labelled "Stage" and sits directly under a badge showing the employment stage, so
   * two different things wore one word. Paul: rename the progress strip, "the employment stage is
   * the badge at the top, so the two aren't confused."
   *
   * It is `outcomeCopy`'s own rather than `moveCopy.stageLabel`, which 2.2 also reads: Paul scoped
   * the rename to 2.5 and 2.6, and renaming the shared constant would have carried it onto 2.2 as
   * well. That 2.2 still says "Stage" beside the same badge is recorded on #388 rather than decided
   * here.
   */
  hiringProgressLabel: 'Hiring progress',
  hiringProgressCaption: 'Set by HR. Hiring progress isn’t clearance to work.',
  /**
   * Shown where the hire date is not recorded yet. Paul calls 2.5 the "Hired on" summary, and a
   * person can reach Onboarding before anybody types a first day — so the heading states what is
   * known rather than printing "Hired on null", and the offer's proposed start is still above it.
   */
  /**
   * PAST TENSE ONLY WHEN IT IS THE PAST. `person.hireDate` is labelled "First day / hire date" on
   * the details form and is required BEFORE activation, so for every person 2.5 is built for it is
   * normally a date that has not arrived yet — and "Hired on <next Monday>" states an event that
   * has not happened. A review of #396 found the PR's own fixture encoding it.
   *
   * Paul's name for the frame is still the "Hired on" summary, and that wording is kept for the
   * case it describes. #373 records that he may want one label for both.
   */
  hiredOn: (formatted: string) => `Hired on ${formatted}`,
  firstDay: (formatted: string) => `First day ${formatted}`,
  hireDateNotSet: 'First day not recorded yet.',
  /**
   * A stored first day that cannot be read as a date. Its own sentence because the alternative is
   * what shipped: `formatRecordedCalendarDate` returns a sentinel for an unreadable value, and
   * interpolating it gave HR "Hired on Date unavailable".
   */
  hireDateUnreadable: 'First day recorded, but the date cannot be read. Re-enter it in the record.',
} as const;

/**
 * One sentence saying where this person got to. Written per stage rather than as one line with the
 * stage interpolated, because "Marcus was withdrawn" and "Marcus is working through onboarding" are
 * not the same sentence with a word swapped.
 */
export function outcomeLede(firstName: string, lifecycle: LifecycleState): string {
  switch (lifecycle) {
    case 'onboarding':
      return `${firstName} moved to Onboarding. The signed offer is filed; this is the record of it.`;
    case 'active':
      return `${firstName} is active. The offer below is the one they were hired on.`;
    case 'inactive':
      return `${firstName} is inactive. The offer below is the one they were hired on.`;
    case 'separated':
      return `${firstName} has separated. The offer below is the one they were hired on.`;
    case 'withdrawn':
      return `${firstName} was withdrawn before starting. The offer below is kept as a record.`;
    default:
      return `The signed offer is filed; this is the record of it.`;
  }
}

export const reviewCopy = {
  crumbSection: 'Offer and stage',
  crumbLeaf: 'Review the signed offer',
  title: 'Review the signed offer',
  statusLabel: 'Status',
  signedLabel: 'Signed in the portal',
  typedNameLabel: 'Typed name',
  filesToLabel: 'Files to',
  noticeTitle: 'Approving files the signed copy',
  approve: 'Approve and file',
  sendBack: 'Send back',
  /** The signing page's own heading, as the frame prints it above the acceptance sentence. */
  acceptanceSentence: 'I accept the offered terms above. My typed name is my electronic signature.',
} as const;

export function reviewLede(firstName: string): string {
  return `${firstName} signed the offer in the portal. Check the signed PDF, then approve it to file the signed copy.`;
}

export function reviewNotice(firstName: string): string {
  return `Once it’s filed, you can move ${firstName} to Onboarding.`;
}

export function sendBackHint(firstName: string): string {
  return `Send back needs a reason. ${firstName} signs again in the portal.`;
}

/** "Signed · waiting for HR" before HR approves it, "Signed · filed" after. */
export const signedStageBadge: Record<SignedOfferStage, { readonly label: string; readonly tone: 'warning' | 'good' }> = {
  'awaiting-hr': { label: 'Signed · waiting for HR', tone: 'warning' },
  filed: { label: 'Signed · filed', tone: 'good' },
};

export interface ReviewCheck {
  readonly key: string;
  readonly label: string;
  /** `null` when there is nothing to judge — rendered as neither pass nor fail. */
  readonly met: boolean | null;
}

/**
 * The two checks in the frame's side panel.
 *
 * Both are DERIVED from the submission the API already sends — the deadline off the offer step's
 * own `submissionDeadline`, the field count off `offerSigningFields`. Neither is a stored verdict,
 * so neither can go stale against the document it describes.
 */
export function reviewChecks(
  submittedAt: string | null,
  deadline: string | null,
  deadlineText: string | null,
  values: Readonly<Record<string, unknown>>,
): readonly ReviewCheck[] {
  const completion = offerSigningCompletion(values);
  const inTime = signedBeforeDeadline(submittedAt, deadline);
  return [
    {
      key: 'deadline',
      // Without a deadline the offer simply had none; the check still shows, saying so, rather
      // than vanishing and leaving HR to wonder whether it passed or was never asked.
      label: deadlineText === null ? 'No Accept-by date was set' : `Signed before Accept by (${deadlineText})`,
      met: inTime,
    },
    {
      key: 'fields',
      label: completion.missing.length === 0
        ? `All ${completion.total} signing fields completed`
        : `${completion.completed} of ${completion.total} signing fields completed — missing ${completion.missing.join(', ')}`,
      met: completion.missing.length === 0,
    },
  ];
}

export const moveCopy = {
  title: 'Offer and stage',
  offerLabel: 'Offer',
  // Filing is stated per offer by `signedLine`, which knows whether it happened; a static caption
  // cannot, so it claims only what signing gives it.
  offerCaption: 'Signed.',
  stageLabel: 'Stage',
  stageCaption: 'Set by HR. Stage isn’t clearance to work.',
  whatHappensLabel: 'What happens when you move',
  positionLabel: 'Position',
  proposedStartLabel: 'Proposed start',
  ratesLabel: 'Rates',
  reasonLabel: 'Reason',
  reasonHint: 'Recorded on the audit trail.',
  // The gate is approval ALONE (ADR-011 Q2 §4: "onboarding does not wait on it"), so naming
  // filing here described a precondition the code does not have.
  footerNote: 'Only allowed once the signed offer is approved.',
  cancel: 'Cancel',
  /**
   * Paul, 2026-09-29 15:45: the button is now **Send Onboarding Items** and it opens a confirm
   * window (2.3) rather than sending straight away. The old label described a STAGE change; what
   * the click actually does is issue a packet of forms to a person, which is the thing worth
   * confirming — and the thing HR gets asked about afterwards.
   */
  move: 'Send Onboarding Items',
  /** The frame's default reason, offered as the starting value. */
  /**
   * The frame's default reason, offered as the starting value — and it goes into the AUDIT TRAIL.
   *
   * It said "approved and filed". A QA run produced a trail reading `file.store-parked` at 19:10:03
   * and then this reason at 19:15:18, four lines apart and contradicting each other. Whatever a
   * screen says, the compliance record must not assert an outcome nobody checked; approval is what
   * the operator just did and is true whatever the filing does next.
   */
  defaultReason: 'Signed offer approved',
} as const;

/**
 * The Rates cell: "$25.00 per visit", "$0.70 per mile" — one line per compensation row.
 *
 * Deliberately shorter than `offerTermsText`, which prints "Visit rate: USD 25.00 per visit" into
 * the offer document itself. This is a summary cell beside Position and Proposed start, and the
 * frame writes it as a bare amount; the full wording stays on the document that is being signed.
 */
export function compensationLines(terms: OfferTerms, locale = 'en-US'): readonly string[] {
  const money = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' });
  return terms.compensation.map((row) => `${money.format(row.amount)} per ${row.unit}`);
}

/**
 * The Offer row on 2.2, built from whichever kind of offer the person actually has.
 *
 * BOTH KINDS, deliberately. The onboarding gate is `acceptedOffer`, which accepts a portal offer
 * approved by HR *and* an `existing-offer` recorded from a signed paper copy. Keying this row on
 * the portal step alone would have shown the frame's screen to one of them and quietly dropped
 * the other back to the generic stage dropdown — a second answer to "may this person move", which
 * is the one thing ADR-011 §4 says there must not be.
 *
 * A paper offer carries no typed terms (`existing-offer` holds the signed copy, a date and HR's
 * note), so those cells are null and the row omits them rather than inventing a position.
 */
export interface SignedOfferSummary {
  readonly badgeLabel: string | null;
  readonly badgeTone: 'warning' | 'good' | null;
  readonly approvedLine: string | null;
  readonly position: string | null;
  readonly proposedStart: string | null;
  readonly rates: readonly string[];
  readonly fileName: string;
  readonly signedLine: string | null;
  readonly storedFileId: string | null;
  readonly legacyUrl: string | null;
}

/**
 * Approval is what this screen is reached on, so it is what the lede states.
 *
 * It said "approved and filed". Filing is now stated once, per offer, by `signedLine`, which reads
 * `StepInstance.storedFileId` and therefore knows; repeating it here would be a second claim made
 * without the signal, and the two could disagree on one screen.
 */
export function moveLede(firstName: string): string {
  return `The signed offer is approved. Move ${firstName} to Onboarding to start their onboarding steps.`;
}

export function moveStepOne(firstName: string): string {
  return `${firstName} moves to Onboarding, with your reason on the audit trail.`;
}

/**
 * "Their onboarding forms are added to the portal. For a Home Health Aide who drives on Agency
 * business, that's 19:" — the frame's sentence, with the role phrase and the total filled in.
 *
 * `rolePhrase` is null when the person has no role set, which the frame has no state for. The
 * sentence drops the clause rather than printing an empty one: a packet with no role behind it is
 * already odd, and inventing "For a  who" would look like a bug.
 */
export function packetIntro(total: number, rolePhrase: string | null): string {
  const scope = rolePhrase === null ? '' : ` For ${rolePhrase},`;
  return `Their onboarding forms are added to the portal.${scope} that’s ${total}:`;
}

export const packetFootnote =
  'The full list is on the documents and checklist section. Uploads a new hire doesn’t have ready are requested later with a secure upload link.';

export function emailChoiceLabel(firstName: string): string {
  return `Email ${firstName} that their onboarding steps are ready`;
}

export function emailChoiceHint(email: string): string {
  return `Sends the Welcome message from Follow-ups & messages to ${email}.`;
}

/**
 * "The portal link they already have keeps working until Sep 28, 2026. Nothing is emailed unless
 * you choose to below."
 *
 * The expiry is only stated when the app actually knows it. The person page holds no current
 * portal link — the Portal step's shared link-and-code dialog issues one on demand and reports
 * that one's expiry — so rather than printing a date derived from nothing, the sentence says where
 * the link is managed. (It named `PortalLinkPanel`, which #502 deleted once the shared dialog from
 * #501 replaced it.)
 */
export function portalLinkNote(expiresAt: string | null, formatted: string | null): string {
  if (expiresAt === null || formatted === null) {
    return 'Any portal link they already have keeps working until it expires; issue or reissue one from the Portal step. Nothing is emailed unless you choose to below.';
  }
  return `The portal link they already have keeps working until ${formatted}. Nothing is emailed unless you choose to below.`;
}

/**
 * Build the Offer row's cells from the record the gate accepted and, for a portal offer, its step.
 *
 * `formatMoment` and `formatDay` are passed in rather than imported so this stays a pure function
 * over its inputs: the tests read the sentences without a locale or a clock of their own.
 */
export function signedOfferSummary(
  record: HrWorkflowRecord,
  step: StepInstance | null,
  format: { readonly moment: (iso: string) => string; readonly day: (iso: string) => string },
): SignedOfferSummary {
  const stage = step === null ? null : signedOfferStage(step);
  const badge = stage === null ? null : signedStageBadge[stage];
  const submissionName = (step?.submission as { fileName?: string } | null)?.fileName;

  if (record.kind === 'offer') {
    return {
      badgeLabel: badge?.label ?? null,
      badgeTone: badge?.tone ?? null,
      approvedLine: step?.reviewedAt == null ? null
        : `Approved ${format.moment(step.reviewedAt)}${step.reviewedBy === null ? '' : ` by ${step.reviewedBy}`}`,
      position: record.terms.position,
      proposedStart: format.day(record.terms.proposedStartDate),
      rates: compensationLines(record.terms),
      fileName: submissionName ?? 'Signed offer',
      /*
        THE FILING CLAUSE FOLLOWS THE FILING, which it did not.

        This said "· filed" unconditionally, one line above the signal that actually answers it.
        `StepInstance.storedFileId` is "set by the `store-file` handler only AFTER the bytes are in
        protected storage and the record appended, so a non-null value means committed" — so a null
        means the commit is queued, parked or failed, and saying "filed" is a claim nobody checked.

        Frame `322:1186` draws the happy path as "Signed Sep 16, 2026 · filed to Employee records" —
        it names a DESTINATION, which is only knowable once the file is committed. So this renders
        the frame's wording when it is true and says what is true otherwise, rather than departing
        from the design. The unfiled wording is not drawn anywhere; Paul may want different words.
      */
      signedLine: step?.submittedAt == null ? null
        : `Signed ${format.moment(step.submittedAt)} · ${step.storedFileId === null
          ? 'not yet filed'
          : `filed to ${fileCategoryLabels[record.terms.target]}`}`,
      storedFileId: step?.storedFileId ?? null,
      legacyUrl: step?.sharePointItemUrl ?? null,
    };
  }

  if (record.kind === 'existing-offer') {
    return {
      // A paper offer has no HR review step to be "waiting for": recording it IS the acceptance,
      // which is why `offerAcceptedAt` reads its `signedAt` directly.
      badgeLabel: signedStageBadge.filed.label,
      badgeTone: 'good',
      approvedLine: null,
      position: null,
      proposedStart: null,
      rates: [],
      fileName: 'Signed offer on file',
      signedLine: `Signed ${format.day(record.signedAt)} · recorded from a signed paper copy`,
      // Both nullable since #233: rows written before it point at a SharePoint link instead.
      storedFileId: (record as { storedFileId?: string | null }).storedFileId ?? null,
      legacyUrl: (record as { sourceUrl?: string | null }).sourceUrl ?? null,
    };
  }

  // Not an offer at all. Unreachable through the container, which filters by kind first.
  return {
    badgeLabel: null, badgeTone: null, approvedLine: null, position: null, proposedStart: null,
    rates: [], fileName: 'Signed offer', signedLine: null, storedFileId: null, legacyUrl: null,
  };
}


/**
 * 2.3, the confirm window, and 2.4, the sent window (Paul, 2026-09-29 15:45; #373).
 *
 * The listing is deliberately the SAME copy 2.2 used to show inline — `packetIntro`,
 * `packetFootnote`, `portalLinkNote` and the email choice — moved rather than rewritten. Paul's
 * change is where the information appears, not what it says, and a second wording of the same facts
 * is how two screens start disagreeing about how many forms get sent.
 */
export const confirmSendCopy = {
  title: 'Send onboarding items',
  /** Named so the reader knows this is the last stop before the candidate is written to. */
  lede: (firstName: string) => `This adds the forms below to ${firstName}'s portal. Check it over before sending.`,
  /**
   * Paul names 3.6 explicitly: it is NOT in this packet and arrives at orientation. Said here
   * because "21 forms" invites the question "is that all of them", and the honest answer is no.
   */
  laterAtOrientation: 'Form 3.6 is not included here — it is completed with them at orientation.',
  cancel: 'Cancel',
  confirm: 'Confirm and send',
} as const;

export const sentCopy = {
  title: 'Onboarding items sent',
  sent: (firstName: string) => `${firstName}'s onboarding items are on their portal.`,
  /** 2.4's only navigation, and the designed way into Row 3 (3.1). */
  goToDocuments: 'Go to Documents & checklist',
  close: 'Close',
} as const;

/**
 * Why the offer email did not go out, in a sentence HR can act on.
 *
 * The server's reason is an operator's phrase ("outside production only synthetic recipients are
 * accepted") and is kept short on purpose; HR reads this one. Only the reasons the code is known to
 * produce are translated, by what they say. Anything else is shown as the server wrote it, after a
 * plain lead-in, rather than guessed at: a wrong translation is worse than a clumsy true sentence.
 */
export function plainDeliveryReason(reason: string | null, firstName: string): string {
  const text = (reason ?? '').toLowerCase();
  if (text.includes('only synthetic recipients')) return `This test site only sends email to made-up test addresses, so nothing was sent to ${firstName}.`;
  if (text.includes('not a usable address') || text.includes('no usable email')) return `${firstName} has no usable email address on file.`;
  if (text.includes('mail is not enabled')) return 'Email is switched off on this site, so nothing was sent.';
  if (text.includes('mail is not configured') || text.includes('no sink is configured')) return 'Email is not set up on this site yet, so nothing was sent.';
  if (text.includes('portal address is not configured')) return 'The portal address is not set up on this site yet, so no link could be made.';
  if (text.includes('provider outcome unknown')) return 'The email service did not confirm the send, so it was held. Check before sending it again.';
  if (text.includes('could not be issued')) return 'The link could not be made, so nothing was sent.';
  return reason === null || reason.trim() === '' ? 'No reason was given.' : `The reason given was: ${reason.trim().replace(/[.\s]+$/, '')}.`;
}

/** The sentence under an offer that is in the portal but was not emailed. */
export function undeliveredNotice(firstName: string, reason: string | null): string {
  return `The offer is in ${firstName}’s portal, but the email did not go out. ${plainDeliveryReason(reason, firstName)} Use “Get the link and code” to hand it over yourself. Making a new link replaces any link ${firstName} already has.`;
}
